import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Pagination } from '@/components/ui/Pagination'
import { EmptyState } from '@/components/ui/EmptyState'
import { ClientsTable, ClientsGrid } from '@/components/admin/clients'
import { useClients } from '@/hooks/useClients'
import { useDebounce } from '@/hooks/useDebounce'
import { Plus, Search, Users, LayoutList, LayoutGrid } from 'lucide-react'
import type { ClientType } from '@/types/client'

const ITEMS_PER_PAGE = 20

export default function ClientsPage() {
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState<ClientType | ''>('')
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list')
  const [page, setPage] = useState(1)
  const [sortBy, setSortBy] = useState('created_at')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const debouncedSearch = useDebounce(search, 300)

  const { data, isLoading } = useClients({
    search: debouncedSearch,
    type: typeFilter || null,
    page,
    limit: ITEMS_PER_PAGE,
    sortBy,
    sortOrder,
  })

  function handleSort(col: string) {
    if (sortBy === col) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(col)
      setSortOrder('asc')
    }
    setPage(1)
  }

  const totalPages = Math.ceil((data?.total || 0) / ITEMS_PER_PAGE)

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-display font-bold text-brown-900">
            Clients
          </h1>
          <p className="mt-1 text-brown-600">
            {data?.total || 0} client{(data?.total || 0) > 1 ? 's' : ''}
          </p>
        </div>
        <Link to="/clients/new">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            Nouveau client
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card padding="sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 min-w-[200px]">
            <Input
              type="search"
              placeholder="Rechercher un client..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              leftIcon={<Search className="w-4 h-4" />}
            />
          </div>
          <div className="w-full sm:w-48">
            <Select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value as ClientType | '')
                setPage(1)
              }}
            >
              <option value="">Tous les types</option>
              <option value="professionnel">Professionnels</option>
              <option value="particulier">Particuliers</option>
            </Select>
          </div>
          <div className="flex gap-1">
            <Button
              variant={viewMode === 'list' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('list')}
              aria-label="Vue liste"
            >
              <LayoutList className="w-4 h-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'primary' : 'outline'}
              size="sm"
              onClick={() => setViewMode('grid')}
              aria-label="Vue grille"
            >
              <LayoutGrid className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </Card>

      {/* Content */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      ) : data?.clients?.length === 0 ? (
        <Card padding="lg">
          <EmptyState
            icon={<Users className="w-12 h-12" />}
            title="Aucun client"
            description={
              search || typeFilter
                ? "Aucun client ne correspond a vos criteres de recherche"
                : "Commencez par ajouter votre premier client"
            }
            action={
              !search && !typeFilter && (
                <Link to="/clients/new">
                  <Button leftIcon={<Plus className="w-4 h-4" />}>
                    Ajouter un client
                  </Button>
                </Link>
              )
            }
          />
        </Card>
      ) : viewMode === 'list' ? (
        <Card padding="none">
          <ClientsTable
            clients={data?.clients || []}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSort={handleSort}
          />
        </Card>
      ) : (
        <ClientsGrid clients={data?.clients || []} />
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  )
}
